import { Component } from '@angular/core';
import { User } from 'src/app/_types/auth.types';
import { InsuranceFlowDetailsResponse } from 'src/app/_types/insurance-flow.types';
import { ProposalDetails } from 'src/app/_types/proposals.type';
import { CustomerAccountType } from 'src/app/enums/customer-account-type.enum';
import {
  getInsuranceTypeIcon,
  getInsuranceTypeLabel,
} from 'src/app/enums/insurance-type.enum';
import {
  DetailsHeaderBadge,
  DetailsHeaderComponent,
} from 'src/app/shared/components/details-header/details-header.component';

import { TaskDetailsService } from '../../task-details.service';

/**
 * Identification block of the task details header (ids, insurance type, customer).
 * Shared by the slide-in modal and the standalone page.
 */
@Component({
  selector: 'app-task-details-header-info',
  standalone: true,
  imports: [DetailsHeaderComponent],
  templateUrl: './task-details-header-info.component.html',
  styleUrl: './task-details-header-info.component.css',
})
export class TaskDetailsHeaderInfoComponent {
  constructor(private readonly taskDetailsService: TaskDetailsService) {}

  protected get isLoading(): boolean {
    return this.taskDetailsService.state.isLoading;
  }

  protected get insuranceFlowData(): InsuranceFlowDetailsResponse | null {
    return this.taskDetailsService.state.insuranceFlowData;
  }

  protected get userProfile(): User | null {
    return this.taskDetailsService.state.userProfile;
  }

  protected get policy(): ProposalDetails | null {
    return this.taskDetailsService.state.policy;
  }

  protected get familyMemberName(): string | null {
    return this.taskDetailsService.state.familyMemberName;
  }

  /** The flow's public id, else the policy's number, else the raw id. */
  protected get headerDisplayId(): string {
    return (
      this.insuranceFlowData?.displayId ||
      this.policy?.policyDetails?.policyId ||
      this.insuranceFlowData?.id ||
      ''
    );
  }

  /** The branch, then the insurer once a policy names one. */
  protected get headerBadges(): DetailsHeaderBadge[] {
    const flow = this.insuranceFlowData;
    if (!flow) {
      return [];
    }
    const badges: DetailsHeaderBadge[] = [
      {
        label: getInsuranceTypeLabel(flow.insuranceType),
        iconUrl: getInsuranceTypeIcon(flow.insuranceType) || null,
      },
    ];
    const category = this.policy?.policyDetails?.categoryDetails;
    if (category) {
      badges.push({
        label: category.categoryName,
        iconUrl: category.categoryLogo || null,
        logo: true,
      });
    }
    return badges;
  }

  /** The insured member for a person, the company otherwise. */
  protected get headerCustomerName(): string | null {
    const user = this.userProfile;
    if (user?.type === CustomerAccountType.PERSONAL) {
      return this.familyMemberName;
    }
    if (user?.type === CustomerAccountType.COMPANY) {
      return (
        user.companyDetails?.companyName ?? `${user.firstName} ${user.lastName}`
      );
    }
    return null;
  }
}
